// frontend/src/pages/recruiter/RecruiterDashboard.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
} from "./../../components/ui/card";
import { Badge } from "./../../components/ui/badge";
import { Button } from "./../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./../../components/ui/tabs";
import { Alert, AlertDescription } from "./../../components/ui/alert";
import Loader from "./../../components/ui/Loader";
import {
 Briefcase,
 Users,
 CheckCircle,
 XCircle,
 Clock,
 Edit,
 Trash2,
 Eye,
 TrendingUp,
 MapPin,
 Calendar,
 ArrowRight,
 BarChart3,
 Plus,
} from "lucide-react";
import api from "./../../api/client";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function RecruiterDashboard() {
 const navigate = useNavigate();

 const [jobs, setJobs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [success, setSuccess] = useState("");

 // Fetch jobs
 useEffect(() => {
 fetchJobs();
 }, []);

 const fetchJobs = async () => {
 try {
 const res = await api.get("/recruiter/jobs");
 setJobs(res.data);
 } catch (err) {
 setError("Failed to load your jobs");
 } finally {
 setLoading(false);
 }
 };

 // Delete job
 const handleDeleteJob = async (jobId) => {
 try {
 await api.delete(`/recruiter/jobs/${jobId}`);
 setSuccess("Job deleted successfully");
 fetchJobs();
 setTimeout(() => setSuccess(""), 3000);
 } catch (err) {
 setError("Failed to delete job");
 }
 };

 // Stats
 const totalJobs = jobs.length;
 const totalApplications = jobs.reduce((sum, job) => sum + (job.applicationCount || 0), 0);
 const pendingApps = jobs.reduce((sum, job) => sum + (job.pendingCount || 0), 0);
 const acceptedApps = jobs.reduce((sum, job) => sum + (job.acceptedCount || 0), 0);
 const pendingJobs = jobs.filter(j => j.status === "PENDING").length;
 const approvedJobs = jobs.filter(j => j.status === "APPROVED").length;

 // Job Status Badge
 const getJobStatusBadge = (status) => {
 const map = {
 PENDING: { variant: "secondary", text: "Pending", icon: Clock, color: "text-yellow-600" },
 APPROVED: { variant: "default", text: "Live", icon: CheckCircle, color: "text-green-600" },
 REJECTED: { variant: "destructive", text: "Rejected", icon: XCircle, color: "text-red-600" },
 };
 const s = map[status] || map.PENDING;
 const Icon = s.icon;
 return (
 <Badge variant={s.variant} className="flex items-center gap-1 text-xs">
 <Icon className="w-3 h-3" />
 {s.text}
 </Badge>
 );
 };

 // Loading
 if (loading) return <Loader />;

 return (
 <div className="min-h-screen ">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
 {/* Header */}
 <div className="mb-8">
 <h1 className="text-3xl md:text-4xl font-bold mb-2">Recruiter Dashboard</h1>
 <p className="text-lg text-muted-foreground">Manage your jobs and track applications</p>
 </div>

 {/* Alerts */}
 {success && (
 <Alert className="mb-6 border-green-500/50 bg-green-500/100/10">
 <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
 <AlertDescription className="text-green-600 dark:text-green-400">{success}</AlertDescription>
 </Alert>
 )}
 {error && (
 <Alert variant="destructive" className="mb-6">
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 {/* Stats Grid - Modern Cards */}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
 {/* Total Jobs Card */}
 <Card className="group cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200 border-l-4 border-l-blue-500" onClick={() => navigate("/recruiter/my-jobs")}>
 <CardContent className="p-6">
 <div className="flex items-center justify-between mb-4">
 <div className="p-3 rounded-xl bg-blue-500/100/10 group-hover:bg-blue-500/100/20 transition-colors">
 <Briefcase className="w-6 h-6 text-blue-600 dark:text-blue-400" />
 </div>
 <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
 </div>
 <div className="space-y-1">
 <p className="text-sm font-medium text-muted-foreground">Total Jobs</p>
 <p className="text-3xl font-bold">{totalJobs}</p>
 <p className="text-xs text-muted-foreground">
 {pendingJobs} pending approval
 </p>
 </div>
 </CardContent>
 </Card>

 {/* Total Applications Card */}
 <Card className="group cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200 border-l-4 border-l-purple-500" onClick={() => navigate("/recruiter/applications/analytics")}>
 <CardContent className="p-6">
 <div className="flex items-center justify-between mb-4">
 <div className="p-3 rounded-xl bg-purple-500/100/10 group-hover:bg-purple-500/100/20 transition-colors">
 <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
 </div>
 <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
 </div>
 <div className="space-y-1">
 <p className="text-sm font-medium text-muted-foreground">Total Applications</p>
 <p className="text-3xl font-bold">{totalApplications}</p>
 <p className="text-xs text-muted-foreground flex items-center gap-1">
 <TrendingUp className="w-3 h-3" />
 Across all jobs
 </p>
 </div>
 </CardContent>
 </Card>

 {/* Manage Applications Card */}
 <Card className="group cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200 border-l-4 border-l-orange-500" onClick={() => navigate("/recruiter/applications")}>
 <CardContent className="p-6">
 <div className="flex items-center justify-between mb-4">
 <div className="p-3 rounded-xl bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
 <Users className="w-6 h-6 text-orange-600 dark:text-orange-400" />
 </div>
 <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
 </div>
 <div className="space-y-1">
 <p className="text-sm font-medium text-muted-foreground">Manage Applications</p>
 <p className="text-3xl font-bold">{pendingApps}</p>
 <p className="text-xs text-muted-foreground">
 Review & manage all
 </p>
 </div>
 </CardContent>
 </Card>

 {/* Accepted Card */}
 <Card className="group cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200 border-l-4 border-l-green-500" onClick={() => navigate("/recruiter/applications/accepted")}>
 <CardContent className="p-6">
 <div className="flex items-center justify-between mb-4">
 <div className="p-3 rounded-xl bg-green-500/100/10 group-hover:bg-green-500/100/20 transition-colors">
 <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
 </div>
 <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
 </div>
 <div className="space-y-1">
 <p className="text-sm font-medium text-muted-foreground">Accepted</p>
 <p className="text-3xl font-bold">{acceptedApps}</p>
 <p className="text-xs text-muted-foreground">
 Successful candidates
 </p>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Tabs */}
 <Tabs defaultValue="all" className="w-full">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
 <TabsList className="h-11 w-full sm:w-auto grid grid-cols-3">
 <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
 <span className="hidden sm:inline">All Jobs</span>
 <span className="sm:hidden">All</span>
 <span className="ml-1">({totalJobs})</span>
 </TabsTrigger>
 <TabsTrigger value="pending" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
 <span className="hidden sm:inline">Pending</span>
 <span className="sm:hidden">Pending</span>
 <span className="ml-1">({pendingJobs})</span>
 </TabsTrigger>
 <TabsTrigger value="approved" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
 <span className="hidden sm:inline">Live</span>
 <span className="sm:hidden">Live</span>
 <span className="ml-1">({approvedJobs})</span>
 </TabsTrigger>
 </TabsList>
 <Button onClick={() => navigate("/recruiter/post-job")} className="w-full sm:w-auto" size="lg">
 <Plus className="w-4 h-4 mr-2" />
 <span className="hidden sm:inline">Post New Job</span>
 <span className="sm:hidden">New Job</span>
 </Button>
 </div>

 {/* Job List */}
 {["all", "pending", "approved"].map((tab) => {
 const filteredJobs =
 tab === "all"
 ? jobs
 : tab === "pending"
 ? jobs.filter(j => j.status === "PENDING")
 : jobs.filter(j => j.status === "APPROVED");

 return (
 <TabsContent key={tab} value={tab} className="space-y-4">
 {filteredJobs.length === 0 ? (
 <Card className="border-2 border-dashed">
 <CardContent className="text-center py-16">
 <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center">
 <Briefcase className="w-10 h-10 text-muted-foreground" />
 </div>
 <h3 className="text-lg font-semibold mb-2">
 {tab === "all"
 ? "No jobs yet"
 : tab === "pending"
 ? "No pending jobs"
 : "No live jobs yet"}
 </h3>
 <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
 {tab === "all"
 ? "Start by posting your first job opening to attract talented candidates"
 : tab === "pending"
 ? "All your jobs have been reviewed"
 : "Post a job to start receiving applications"}
 </p>
 {tab === "all" && (
 <Button onClick={() => navigate("/recruiter/post-job")} size="lg">
 <Plus className="w-4 h-4 mr-2" />
 Post Your First Job
 </Button>
 )}
 </CardContent>
 </Card>
 ) : (
 filteredJobs.map((job) => {
 return (
 <Card key={job.id} className="hover:shadow-lg transition-all duration-200 overflow-hidden group">
 <div className="flex flex-col md:flex-row">
 {/* Left Side - Job Info */}
 <div className="flex-1 p-6">
 <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
 <div className="flex-1">
 <div className="flex flex-wrap items-center gap-2 mb-2">
 <h3
 className="text-xl md:text-2xl font-bold cursor-pointer hover:text-primary transition-colors hover:underline decoration-2 underline-offset-4"
 onClick={() => navigate(`/recruiter/jobs/${job.id}`)}
 >
 {job.title}
 </h3>
 {getJobStatusBadge(job.status)}
 </div>
 <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm text-muted-foreground">
 <div className="flex items-center gap-1">
 <MapPin className="w-4 h-4" />
 {job.location}
 </div>
 <div className="flex items-center gap-1">
 <Calendar className="w-4 h-4" />
 {new Date(job.createdAt).toLocaleDateString("en-US", {
 month: "short",
 day: "numeric",
 year: "numeric",
 })}
 </div>
 </div>
 </div>
 </div>

 {/* Applicant Status Badges */}
 <div className="flex flex-wrap gap-2 mb-4">
 <Badge variant="outline" className="bg-yellow-500/10 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300">
 <Clock className="w-3 h-3 mr-1" />
 {job.pendingCount || 0} Pending
 </Badge>
 <Badge variant="outline" className="bg-green-500/10 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300">
 <CheckCircle className="w-3 h-3 mr-1" />
 {job.acceptedCount || 0} Accepted
 </Badge>
 <Badge variant="outline" className="bg-red-50 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
 <XCircle className="w-3 h-3 mr-1" />
 {job.rejectedCount || 0} Rejected
 </Badge>
 </div>

 {/* Action Buttons */}
 <div className="flex flex-col sm:flex-row flex-wrap gap-2">
 <Button
 size="sm"
 onClick={() => navigate(`/recruiter/jobs/${job.id}/applicants`)}
 className="w-full sm:w-auto"
 >
 <Users className="w-4 h-4 mr-2" />
 View Applicants
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => navigate(`/recruiter/jobs/${job.id}`)}
 className="w-full sm:w-auto"
 >
 <Eye className="w-4 h-4 mr-2" />
 Details
 </Button>
 {job.status === "PENDING" && (
 <>
 <Button
 size="sm"
 variant="outline"
 onClick={() => navigate(`/recruiter/jobs/${job.id}/edit`)}
 className="w-full sm:w-auto"
 >
 <Edit className="w-4 h-4 mr-2" />
 Edit
 </Button>
 <ConfirmDialog
 trigger={
 <Button size="sm" variant="outline" className="w-full sm:w-auto text-destructive hover:text-destructive">
 <Trash2 className="w-4 h-4 mr-2" />
 Delete
 </Button>
 }
 title="Delete Job"
 description="Are you sure you want to delete this job? This action cannot be undone."
 onConfirm={() => handleDeleteJob(job.id)}
 confirmText="Delete"
 variant="destructive"
 />
 </>
 )}
 </div>
 </div>

 {/* Right Side - Application Count */}
 <div className=" p-6 flex items-center justify-center md:min-w-[180px] border-t md:border-t-0 md:border-l">
 <div className="text-center">
 <div className="text-5xl font-bold text-primary mb-2">{job.applicationCount || 0}</div>
 <p className="text-sm text-muted-foreground font-medium">Applications</p>
 </div>
 </div>
 </div>
 </Card>
 );
 })
 )}
 </TabsContent>
 );
 })}
 </Tabs>
 </div>
 </div>
 );
}
